import type { FC } from 'react';
import { useNavigate } from 'react-router';
import type { useGetCustomRoleById } from 'src/hooks/queries/custom-role/useGetCustomRoleById';

import { useUpdateCustomRole } from '@/hooks/mutations/custom-role';
import { settingsCustomRolesUrl } from '@/utils/urls';

import type { AvailableRole } from '../../../availableRoles';
import CustomRoleForm from '../../../forms/CustomRoleForm';
import type { CustomRoleFormFields } from '../../../forms/customRoleSchema';

type GetCustomRoleByIdData = NonNullable<
  ReturnType<typeof useGetCustomRoleById>['data']
>;

type Props = {
  customRole: GetCustomRoleByIdData['custom_role'][number];
  availableRoles: AvailableRole[];
};

const Tab: FC<Props> = ({ customRole, availableRoles }) => {
  const navigate = useNavigate();
  const { updateCustomRole } = useUpdateCustomRole();

  const customRoleValues = {
    Name: customRole.RoleName,
    Description: customRole.Description ?? undefined,
    RoleKeys: customRole.customRoleAssignments.map((cra) => cra.RoleTypeKey),
    UserIds: customRole.customRoleUsers.map((cru) => ({
      type: 'user' as const,
      value: cru.UserId,
    })),
  };

  const onSave = async (data: CustomRoleFormFields) => {
    const result = await updateCustomRole({
      id: customRole.Id,
      name: data.Name,
      description: data.Description,
      roleKeys: data.RoleKeys,
      userIds: data.UserIds.map((c) => c.value),
    });
    if (result.customRoleUpdate?.affected_rows !== 1) {
      throw new Error(
        'Records not updated. Record may have been updated by another user'
      );
    }
    navigate(settingsCustomRolesUrl());
  };

  const onDismiss = () => navigate(settingsCustomRolesUrl());

  return (
    <CustomRoleForm
      readOnly={false}
      onSave={onSave}
      onDismiss={onDismiss}
      values={customRoleValues}
      availableRoles={availableRoles}
    />
  );
};

export default Tab;
